import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DateFormat } from '@equip.health/ui';

import AnalyticsEvents from '~/analytics';
import {
  calculateAverageEntryCount,
  getCurrentAndPreviousMonth,
  getMonthDateRange,
  getWeekDateRange,
  sumEntryCounts,
} from '~/components/maud/patients/profile/smt/utils';
import { toLocalMidnight } from '~/lib/util/date.util';
import {
  useGetSMTSummaryQuery,
  useLazyGetSMTSummaryQuery,
} from '~/store/services/SMT';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';

interface UseSMTSummaryReturn {
  activeDates: Date[];
  averageLogCount: number;
  datesWithLogs: Date[];
  handleMonthChange: (date: Date) => void;
  totalLogCount: number;
  weekEntries: SMTSummaryEntry[];
}

/**
 * Custom hook for managing SMT summary data fetching and state.
 * Handles week summary, month summaries, and dynamic month fetching on navigation.
 *
 * @param patientId - The patient ID to fetch data for
 * @param isSMTEnabledForPatient - Whether SMT is enabled for the patient
 * @param monthsToFetchOnNavigation - Number of months to fetch when navigating (default: 2)
 * @returns Object containing week/month data and handlers
 */
const useSMTSummary = (
  patientId: string,
  isSMTEnabledForPatient: boolean,
  monthsToFetchOnNavigation: number = 2,
): UseSMTSummaryReturn => {
  const { trackEvent } = useAnalytics();
  const isMountedRef = useRef<boolean>(true);

  // Track which months have been fetched (keyed by "YYYY-MM")
  const [fetchedMonths, setFetchedMonths] = useState<Set<string>>(new Set());
  const [monthsToFetch, setMonthsToFetch] = useState<Set<string>>(new Set());
  const [accumulatedEntries, setAccumulatedEntries] = useState<
    SMTSummaryEntry[]
  >([]);
  const [fetchMonthSummary] = useLazyGetSMTSummaryQuery();

  // Cleanup: mark component as unmounted
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Calculate week date range (7 days including today, going back 6 days)
  const { endDate: weekEndDate, startDate: weekStartDate } = getWeekDateRange();

  // Calculate current and previous month for initial load
  const { currentMonth, previousMonth } = getCurrentAndPreviousMonth();

  // Initialize months to fetch (current and previous)
  // Fetching 2 months allows the user to see the "Previous Month" button in the date picker
  // which, when clicked, will fetch additional months as needed
  useEffect(() => {
    if (isSMTEnabledForPatient) {
      setMonthsToFetch(new Set([currentMonth, previousMonth]));
    }
  }, [currentMonth, isSMTEnabledForPatient, previousMonth]);

  // Fetch week summary data
  const { data: weekSummaryData } = useGetSMTSummaryQuery(
    {
      endDate: weekEndDate,
      patientId,
      startDate: weekStartDate,
    },
    {
      skip: !isSMTEnabledForPatient || !weekStartDate || !weekEndDate,
    },
  );

  // Fetch data for current month
  const currentMonthRange = getMonthDateRange(currentMonth);
  const { data: currentMonthData, refetch: refetchCurrentMonth } =
    useGetSMTSummaryQuery(
      {
        endDate: currentMonthRange.endDate,
        patientId,
        startDate: currentMonthRange.startDate,
      },
      {
        skip:
          !isSMTEnabledForPatient ||
          !monthsToFetch.has(currentMonth) ||
          fetchedMonths.has(currentMonth),
      },
    );

  // Fetch data for previous month
  const previousMonthRange = getMonthDateRange(previousMonth);
  const { data: previousMonthData, refetch: refetchPreviousMonth } =
    useGetSMTSummaryQuery(
      {
        endDate: previousMonthRange.endDate,
        patientId,
        startDate: previousMonthRange.startDate,
      },
      {
        skip:
          !isSMTEnabledForPatient ||
          !monthsToFetch.has(previousMonth) ||
          fetchedMonths.has(previousMonth),
      },
    );

  // Update accumulated entries when month data arrives
  const updateAccumulatedEntries = useCallback(
    (data: { entries: SMTSummaryEntry[] } | undefined, monthKey: string) => {
      if (!isMountedRef.current) {
        return;
      }
      if (data?.entries && !fetchedMonths.has(monthKey)) {
        setFetchedMonths((prev) => new Set(prev).add(monthKey));
        setAccumulatedEntries((prev) => {
          const existingDates = new Set(prev.map((e) => e.date));
          const newEntries = data.entries.filter(
            (e) => !existingDates.has(e.date),
          );
          return [...prev, ...newEntries];
        });
      }
    },
    [fetchedMonths],
  );

  // Accumulate entries when month data arrives
  useEffect(() => {
    updateAccumulatedEntries(currentMonthData, currentMonth);
  }, [currentMonthData, currentMonth, updateAccumulatedEntries]);

  useEffect(() => {
    updateAccumulatedEntries(previousMonthData, previousMonth);
  }, [previousMonthData, previousMonth, updateAccumulatedEntries]);

  // Function to fetch a specific month's data
  const fetchMonthData = async (monthKey: string) => {
    if (!isMountedRef.current) {
      return;
    }

    if (fetchedMonths.has(monthKey)) {
      return; // Already fetched
    }

    // If it's current or previous month, use the existing queries
    if (monthKey === currentMonth) {
      if (!isMountedRef.current) return;
      setMonthsToFetch((prev) => new Set(prev).add(monthKey));
      refetchCurrentMonth();
      return;
    }
    if (monthKey === previousMonth) {
      if (!isMountedRef.current) return;
      setMonthsToFetch((prev) => new Set(prev).add(monthKey));
      refetchPreviousMonth();
      return;
    }

    // For other months, use lazy query
    const monthRange = getMonthDateRange(monthKey);
    try {
      const result = await fetchMonthSummary({
        endDate: monthRange.endDate,
        patientId,
        startDate: monthRange.startDate,
      }).unwrap();
      if (isMountedRef.current) {
        updateAccumulatedEntries({ entries: result.entries }, monthKey);
      }
    } catch (error) {
      // Silently handle errors - the month will remain unfetched
      console.error('Failed to fetch SMT Summary month data:', error);
    }
  };

  // Function to fetch a range of months starting from a given month
  const fetchMonthRange = async (startMonthDate: Date) => {
    // Fetch the target month and the previous N months (where N is monthsToFetchOnNavigation)
    const monthKeys = Array.from(
      { length: monthsToFetchOnNavigation + 1 },
      (_, i) =>
        DateTime.fromJSDate(startMonthDate)
          .minus({ months: i })
          .toFormat(DateFormat.shortYearMonth),
    );
    await Promise.all(monthKeys.map((monthKey) => fetchMonthData(monthKey)));
  };

  // Handler for month change in date picker
  const handleMonthChange = (date: Date) => {
    // The date parameter represents the end of the newly selected month
    // Fetch this month and the previous N months (configurable via monthsToFetchOnNavigation)
    fetchMonthRange(date);
    trackEvent(AnalyticsEvents.datePickerMonthChanged, {
      date: date.toISOString(),
      patientId,
    });
  };

  // Process week data for SMTWeekLog
  const { averageLogCount, datesWithLogs, totalLogCount, weekEntries } =
    useMemo(() => {
      const entries = weekSummaryData?.entries ?? [];
      return {
        averageLogCount: calculateAverageEntryCount(entries),
        datesWithLogs: entries
          .filter((entry) => entry.entryCount > 0)
          .map((entry) => toLocalMidnight(new Date(entry.date))),
        totalLogCount: sumEntryCounts(entries),
        weekEntries: entries,
      };
    }, [weekSummaryData]);

  // Process accumulated entries for SMTDatePicker
  const activeDates = useMemo(
    () =>
      accumulatedEntries
        .filter((entry) => entry.entryCount > 0)
        .map((entry) => toLocalMidnight(new Date(entry.date))),
    [accumulatedEntries],
  );

  return {
    activeDates,
    averageLogCount,
    datesWithLogs,
    handleMonthChange,
    totalLogCount,
    weekEntries,
  };
};

export default useSMTSummary;
